import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";
import {
  listBadges,
  createBadge,
  updateBadge,
  deleteBadge,
  uploadBadgeIcon,
  type Badge,
} from "../../services/badges";

// Every criteria kind the award-checker (backend/src/lib/badges.ts)
// understands, grouped for readability. Values are starter templates the
// admin can tweak after clicking.
const CRITERIA_TEMPLATES: Record<string, any> = {
  // Quiz
  first_perfect: { kind: "first_perfect" },
  perfect_streak: { kind: "perfect_streak", count: 3 },
  speed_master: { kind: "speed_master", threshold_ms: 2000, min_correct: 5 },
  correct_answers_total: { kind: "correct_answers_total", count: 100 },
  quiz_completed_total: { kind: "quiz_completed_total", count: 10 },
  flawless_quiz: { kind: "flawless_quiz" },
  perfect_on_difficulty: { kind: "perfect_on_difficulty", difficulty: "hard" },
  improved_retake: { kind: "improved_retake" },
  single_answer_speed: { kind: "single_answer_speed", threshold_ms: 500 },
  fast_answers_total: { kind: "fast_answers_total", threshold_ms: 1000, count: 10 },
  early_adopter: { kind: "early_adopter", hours: 24 },
  // Attendance
  attendance_total: { kind: "attendance_total", count: 10 },
  attendance_streak: { kind: "attendance_streak", count: 5 },
  perfect_month: { kind: "perfect_month", min_sessions: 4 },
  // Video
  videos_completed_total: { kind: "videos_completed_total", count: 10 },
  // Points / rewards
  points_total: { kind: "points_total", count: 1000 },
  reward_count: { kind: "reward_count", count: 1 },
  // Competition / prizes
  prize_total: { kind: "prize_total", count: 1 },
  prize_rank: { kind: "prize_rank", period_type: "month", rank: 1 },
  // Account / consistency
  account_age_days: { kind: "account_age_days", days: 365 },
  comeback: { kind: "comeback", days: 30 },
  active_days_streak: { kind: "active_days_streak", count: 7 },
};

const initialForm = () => ({
  code: "",
  name: "",
  description: "",
  icon_emoji: "🏆",
  icon_image_url: "" as string,
  color: "#8b5cf6",
  tier: "bronze",
  category: "",
  criteria: JSON.stringify(CRITERIA_TEMPLATES.first_perfect, null, 2),
  is_active: true,
});

const TIER_OPTIONS = ["bronze", "silver", "gold", "platinum"];
const CATEGORY_OPTIONS = ["quiz", "attendance", "video", "points", "competition", "special"];

export default function BadgesAdmin() {
  const [badges, setBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Badge | null>(null);
  const [form, setForm] = useState(initialForm());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listBadges();
      setBadges(data);
    } catch (e) {
      console.error(e);
      toast.error("Nu s-au putut încărca insignele");
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm(initialForm());
    setModalOpen(true);
  };

  const openEdit = (b: Badge) => {
    setEditing(b);
    setForm({
      code: b.code,
      name: b.name,
      description: b.description,
      icon_emoji: b.icon_emoji || "🏆",
      icon_image_url: b.icon_image_url || "",
      color: b.color,
      tier: b.tier || "bronze",
      category: b.category || "",
      criteria: JSON.stringify(b.criteria, null, 2),
      is_active: b.is_active,
    });
    setModalOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      let criteria: any;
      try {
        criteria = JSON.parse(form.criteria);
      } catch {
        toast.error("JSON invalid pentru Criteria");
        setSaving(false);
        return;
      }
      const payload = {
        code: form.code,
        name: form.name,
        description: form.description,
        icon_emoji: form.icon_emoji,
        icon_image_url: form.icon_image_url || null,
        color: form.color,
        tier: form.tier,
        category: form.category || null,
        criteria,
        is_active: form.is_active,
      };
      if (editing) {
        await updateBadge(editing.id, payload);
        toast.success("Insigna actualizată");
      } else {
        await createBadge(payload);
        toast.success("Insigna creată");
      }
      setModalOpen(false);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const handleIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadBadgeIcon(file);
      setForm((f) => ({ ...f, icon_image_url: url }));
      toast.success("Imagine încărcată");
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Eroare la încărcarea imaginii");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (b: Badge) => {
    if (!window.confirm(`Sigur ștergi insigna "${b.name}"? Toți câștigătorii o pierd.`)) return;
    try {
      await deleteBadge(b.id);
      toast.success("Insigna ștearsă");
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Eroare la ștergere");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-[1500px] mx-auto">
        <AdminPageHeader
          eyebrow="Conținut și Educație"
          title="Insigne"
          subtitle="Catalogul de badges pentru sportivi"
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 15a7 7 0 100-14 7 7 0 000 14zM8.5 13.5L7 22l5-3 5 3-1.5-8.5" />
            </svg>
          }
          actions={
            <AdminButton
              onClick={openNew}
              icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}
            >
              Insignă nouă
            </AdminButton>
          }
        />

        {loading ? (
          <div className="text-center py-12 text-slate-500">Se încarcă...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {badges.map((b) => (
              <div
                key={b.id}
                className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex gap-3 items-start"
              >
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl flex-shrink-0 overflow-hidden"
                  style={{ backgroundColor: `${b.color}20`, color: b.color }}
                >
                  {b.icon_image_url ? (
                    <img src={b.icon_image_url} alt={b.name} className="w-full h-full object-contain p-1" />
                  ) : (
                    b.icon_emoji || "🏆"
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-bold text-slate-800 truncate">{b.name}</p>
                    {!b.is_active && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-full">
                        inactivă
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 truncate">{b.code}</p>
                  <p className="text-xs text-slate-600 mt-1 line-clamp-2">{b.description}</p>
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-100">
                      {b.tier}
                    </span>
                    {b.category && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 capitalize">
                        {b.category}
                      </span>
                    )}
                    <span className="text-[10px] text-slate-400">🏅 {b.earner_count ?? 0}</span>
                  </div>
                  <div className="mt-2 flex gap-2">
                    <button onClick={() => openEdit(b)} className="text-xs text-indigo-600 hover:underline">Editează</button>
                    <button onClick={() => remove(b)} className="text-xs text-red-600 hover:underline">Șterge</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-800">
                {editing ? "Editează insigna" : "Insignă nouă"}
              </h3>
              <button onClick={() => setModalOpen(false)} className="p-2 hover:bg-slate-100 rounded-full">
                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Cod unic (ex: speed_master_2s)</label>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-900"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Nume afișat</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-900"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Descriere</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-900"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Emoji</label>
                  <input
                    type="text"
                    value={form.icon_emoji}
                    onChange={(e) => setForm({ ...form, icon_emoji: e.target.value })}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-900"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Culoare</label>
                  <input
                    type="color"
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    className="w-full h-10 rounded-lg cursor-pointer"
                  />
                </div>
              </div>

              {/* Custom icon image — overrides the emoji when set. */}
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Iconiță imagine (PNG/SVG) — <span className="text-slate-500">opțional, înlocuiește emoji-ul</span>
                </label>
                <div className="flex items-center gap-3">
                  <div
                    className="w-14 h-14 rounded-lg flex items-center justify-center text-2xl flex-shrink-0 overflow-hidden border border-slate-200"
                    style={{ backgroundColor: `${form.color}20`, color: form.color }}
                  >
                    {form.icon_image_url ? (
                      <img src={form.icon_image_url} alt="previzualizare" className="w-full h-full object-contain p-1" />
                    ) : (
                      form.icon_emoji || "🏆"
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className="inline-block px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-medium cursor-pointer">
                      {uploading ? "Se încarcă..." : form.icon_image_url ? "Schimbă imaginea" : "Încarcă imagine"}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,.svg"
                        onChange={handleIconUpload}
                        disabled={uploading}
                        className="hidden"
                      />
                    </label>
                    {form.icon_image_url && (
                      <button
                        onClick={() => setForm({ ...form, icon_image_url: "" })}
                        className="ml-2 text-xs text-red-600 hover:underline"
                      >
                        Elimină
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Tier (raritate)</label>
                  <select
                    value={form.tier}
                    onChange={(e) => setForm({ ...form, tier: e.target.value })}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-900 capitalize"
                  >
                    {TIER_OPTIONS.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Categorie</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-900 capitalize"
                  >
                    <option value="">—</option>
                    {CATEGORY_OPTIONS.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Criteria (JSON) — <span className="text-slate-500">alege un template ca punct de start:</span>
                </label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {Object.keys(CRITERIA_TEMPLATES).map((k) => (
                    <button
                      key={k}
                      onClick={() => setForm({ ...form, criteria: JSON.stringify(CRITERIA_TEMPLATES[k], null, 2) })}
                      className="px-2 py-1 text-[11px] bg-slate-100 hover:bg-slate-200 text-slate-700 rounded"
                    >
                      {k}
                    </button>
                  ))}
                </div>
                <textarea
                  value={form.criteria}
                  onChange={(e) => setForm({ ...form, criteria: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                Activă (se poate câștiga)
              </label>
            </div>
            <div className="p-5 border-t border-slate-100 flex gap-2 justify-end">
              <button onClick={() => setModalOpen(false)} disabled={saving} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg font-medium text-sm">
                Anulează
              </button>
              <button onClick={save} disabled={saving} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium text-sm disabled:opacity-50">
                {saving ? "Se salvează..." : "Salvează"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
